/**
 * Product form (PDP, A案): resolves the selected color/size combination to a
 * variant, swaps the product images, updates the size guide, and keeps the
 * URL in sync. No framework — plain DOM, same style as semantic-search.js.
 *
 * The size always starts unselected (the color comes from ?variant= or the
 * first available variant), so nobody buys a size they never chose. The hidden
 * `id` input is disabled until a size is picked; without JS, a <noscript>
 * <select name="id" required> in Liquid carries the choice instead.
 */
(() => {
  const form = document.getElementById('product-form');
  if (!form) return;

  const variantsScript = form.querySelector('[data-product-variants]');
  const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
  if (variants.length === 0) return;

  const idInput = form.querySelector('[data-variant-id]');
  const addButton = form.querySelector('[data-add-to-cart]');
  const priceEl = form.querySelector('[data-role="price"]');
  const colorGroup = form.querySelector('[data-role="colors"]');
  const sizeGroup = form.querySelector('[data-role="sizes"]');
  const colorButtons = colorGroup ? [...colorGroup.querySelectorAll('[data-color]')] : [];
  const sizeButtons = sizeGroup ? [...sizeGroup.querySelectorAll('[data-size]')] : [];
  // JS が動いたので、JS 無し用の select を外してサイズボタンを出す
  // (script の到着前にそれで選ばれていたサイズは引き継ぐ)
  const fallbackSelect = form.querySelector('[data-size-fallback]');
  const preselected = fallbackSelect && variants.find((v) => String(v.id) === fallbackSelect.value);
  form.querySelectorAll('[data-size-fallback]').forEach((el) => el.remove());
  if (sizeGroup) sizeGroup.hidden = false;
  const colorNameEl = form.querySelector('[data-role="color-name"]');
  const sizeNameEl = form.querySelector('[data-role="size-name"]');
  const measureLenEl = form.querySelector('[data-role="measure-len"]');
  const measureWidEl = form.querySelector('[data-role="measure-wid"]');

  const heroImg = document.querySelector('[data-role="hero-image"]');
  const cropImg = document.querySelector('[data-role="crop-image"]');
  const cropAltImg = document.querySelector('[data-role="crop-alt-image"]');

  const titleEl = document.querySelector('.product__title');
  const productTitle = titleEl ? titleEl.textContent.trim() : '';

  // Color -> a representative image URL, built from the variant data itself
  // so this isn't hardcoded to exactly two colors.
  const colorImages = {};
  variants.forEach((v) => {
    if (v.color && v.image && !colorImages[v.color]) colorImages[v.color] = v.image;
  });

  const pressedOrFirst = (buttons) => buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || buttons[0];
  const initialColorBtn = pressedOrFirst(colorButtons);

  const state = {
    color: initialColorBtn ? initialColorBtn.dataset.color : '',
    size: preselected && preselected.available ? preselected.size : '',
  };
  // 買うボタンを押したのにサイズが未選択だった。サイズが選ばれるまで、サイズの欄で「選んでください」と示す
  let sizeNeeded = false;
  const sizeOption = sizeGroup ? sizeGroup.closest('.product__option') : null;

  const initialVariant = variants.find((v) => String(v.id) === variantsScript.dataset.initialVariant);
  const initialOptions = initialVariant?.options || [];
  const colorOptionIndex = Number(variantsScript.dataset.colorOptionIndex);
  const sizeOptionIndex = Number(variantsScript.dataset.sizeOptionIndex);
  const matchesFixedOptions = (variant) =>
    variant.options.every(
      (option, index) =>
        index === colorOptionIndex || index === sizeOptionIndex || option === initialOptions[index]
    );

  const findVariant = () =>
    variants.find(
      (v) =>
        (!state.color || v.color === state.color) &&
        (sizeButtons.length === 0 || (state.size && v.size === state.size)) &&
        matchesFixedOptions(v)
    );

  // Size unselected: a representative variant of the chosen color (price, URL).
  const findColorVariant = () => {
    const ofColor = variants.filter((v) => (!state.color || v.color === state.color) && matchesFixedOptions(v));
    return ofColor.find((v) => v.available) || ofColor[0];
  };

  const updatePressed = (buttons, key) => {
    buttons.forEach((btn) => {
      const value = key === 'color' ? btn.dataset.color : btn.dataset.size;
      btn.setAttribute('aria-pressed', String(value === state[key]));
    });
  };

  const updateSizeAvailability = () => {
    sizeButtons.forEach((btn) => {
      const size = btn.dataset.size;
      const match = variants.find(
        (v) => (!state.color || v.color === state.color) && v.size === size && matchesFixedOptions(v)
      );
      const disabled = !match || !match.available;
      btn.disabled = disabled;
    });
  };

  const updateColorAvailability = () => {
    colorButtons.forEach((btn) => {
      const color = btn.dataset.color;
      const match = variants.find((v) => v.color === color && matchesFixedOptions(v));
      btn.disabled = !match || !match.available;
    });
  };

  const updateImages = () => {
    const heroSrc = colorImages[state.color];
    if (heroSrc) {
      if (heroImg) {
        heroImg.src = heroSrc;
        heroImg.alt = `${productTitle} ${state.color}`.trim();
      }
      if (cropImg) {
        cropImg.src = heroSrc;
        cropImg.alt = `${productTitle} プリント部分`.trim();
      }
    }
    if (cropAltImg) {
      const otherColor = Object.keys(colorImages).find((c) => c !== state.color);
      if (otherColor) {
        cropAltImg.src = colorImages[otherColor];
        cropAltImg.alt = `${productTitle} ${otherColor}`.trim();
      }
    }
  };

  const updateMeasure = () => {
    const row = state.size ? document.querySelector(`[data-size-row="${CSS.escape(state.size)}"]`) : null;
    if (measureLenEl) measureLenEl.textContent = row ? `${row.dataset.len} cm` : '—';
    if (measureWidEl) measureWidEl.textContent = row ? `${row.dataset.wid} cm` : '—';
    document.querySelectorAll('[data-size-row]').forEach((tr) => {
      tr.setAttribute('data-active', String(tr.dataset.sizeRow === state.size));
    });
  };

  const updateUrl = (variant) => {
    if (!variant || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  const render = () => {
    const needsSize = sizeButtons.length > 0;
    if (state.size && !findVariant()?.available) state.size = '';
    const variant = findVariant();
    const shown = variant || findColorVariant();

    updatePressed(colorButtons, 'color');
    updatePressed(sizeButtons, 'size');
    updateColorAvailability();
    updateSizeAvailability();
    updateImages();
    updateMeasure();

    if (colorNameEl) colorNameEl.textContent = state.color;
    if (state.size) sizeNeeded = false;
    if (sizeNameEl) sizeNameEl.textContent = state.size || (sizeNeeded ? 'サイズを選んでください' : '選んでください');
    if (sizeOption) sizeOption.classList.toggle('is-needed', sizeNeeded);

    if (idInput) {
      const nextId = variant ? String(variant.id) : needsSize ? '' : String(shown?.id ?? '');
      idInput.disabled = !nextId;
      if (idInput.value !== nextId) {
        idInput.value = nextId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (priceEl && shown) priceEl.textContent = shown.price;

    if (addButton) {
      const purchasable = !!variant && variant.available;
      const waitingForSize = !variant && !!findColorVariant()?.available;
      // サイズ待ちのボタンは押せる（押すとサイズの欄へ移る）。画面の下端に追従するので、押せない灰色にしない
      addButton.disabled = !purchasable && !waitingForSize;
      addButton.textContent = purchasable ? 'カートに入れる' : waitingForSize ? 'サイズを選ぶ' : '売り切れ';
    }

    updateUrl(shown);
  };

  if (colorGroup) {
    colorGroup.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-color]');
      if (!btn || btn.disabled) return;
      state.color = btn.dataset.color;
      render();
    });
  }

  if (sizeGroup) {
    sizeGroup.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size]');
      if (!btn || btn.disabled) return;
      state.size = btn.dataset.size;
      render();
    });
  }

  // サイズを選ばずに押されたら、送らずにサイズの欄へ案内する（id は disabled で、送っても買えない）
  form.addEventListener('submit', (e) => {
    if (sizeButtons.length === 0 || state.size) return;
    e.preventDefault();
    sizeNeeded = true;
    render();
    if (sizeOption) {
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      sizeOption.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      // キーボード・スクリーンリーダーの利用者も、買うボタンに取り残されずサイズから選べるようにする
      const firstSize = sizeButtons.find((b) => !b.disabled);
      if (firstSize) firstSize.focus({ preventScroll: true });
    }
  });

  render();
})();
